import { useEffect, useRef, useState } from 'react';
import { sendHeartbeat } from '../services/apiClient';

/**
 * Sends a lightweight presence "heartbeat" while the app is open so the admin
 * can see who is online / away / logged-out. Also surfaces the user's unread
 * message count and whether messaging is currently enabled.
 *
 * - Beats every ~20s while the tab is visible (status: active).
 * - On the tab being hidden, reports 'away'.
 * - Stops when the user logs out (no user id).
 */
export function usePresenceHeartbeat(user) {
  const [state, setState] = useState({ enabled: false, unread: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;
    const beat = async () => {
      const status = typeof document !== 'undefined' && document.hidden ? 'away' : 'active';
      const result = await sendHeartbeat(status);
      if (!cancelled && result?.ok) {
        setState({ enabled: Boolean(result.enabled), unread: Number(result.unread) || 0 });
      }
    };

    beat();
    timerRef.current = setInterval(beat, 20000);
    const onVisibility = () => beat();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]);

  return state;
}

export default usePresenceHeartbeat;
